import { AnimatePresence, motion } from 'framer-motion';
import { BookOpen, Code2, Coffee, User, X } from 'lucide-react';
import type { ReactNode } from 'react';
import Button from '@components/ui/Button';
import Icon from '@components/ui/Icon';
import { useUIStore } from '@stores/useUIStore';

const KOFI_URL = 'https://ko-fi.com/studioshell';

/**
 * AboutModal — product info, credits strip, version tiles, and
 * a Donate external link. Triggered from the TopBar logo,
 * the "About" button, or the splash "Learn more" link. Closed by the
 * close button, outside click, or Escape key.
 */
export default function AboutModal() {
  const visible = useUIStore((s) => s.isAboutVisible);
  const close = useUIStore((s) => s.dismissAbout);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          key="about-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2, ease: 'easeOut' }}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 600,
            display: 'grid',
            placeItems: 'center',
            padding: 'var(--space-2xl)',
            background: 'rgba(0, 0, 0, 0.55)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
          }}
          onClick={close}
        >
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-labelledby="about-title"
            initial={{ opacity: 0, y: 10, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 6, scale: 0.98 }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
            onClick={(e) => e.stopPropagation()}
            style={{
              width: '100%',
              maxWidth: 540,
              padding: 'var(--space-3xl) 28px 28px',
              background: 'var(--bg-panel)',
              border: '1px solid var(--border-mid)',
              borderRadius: 'var(--radius-lg)',
              boxShadow:
                '0 24px 60px rgba(0, 0, 0, 0.5), 0 0 40px rgba(112, 148, 36, 0.05)',
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--space-lg)',
              position: 'relative',
            }}
          >
            {/* Close */}
            <button
              type="button"
              onClick={close}
              aria-label="Close about dialog"
              style={{
                position: 'absolute',
                top: 12,
                right: 12,
                width: 28,
                height: 28,
                borderRadius: 'var(--radius-sm)',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                display: 'grid',
                placeItems: 'center',
                color: 'var(--text-hint)',
              }}
            >
              <Icon icon={X} size={14} />
            </button>

            {/* Header — logo + wordmark */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-md)' }}>
              <div
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: 'var(--radius-md)',
                  background: 'var(--accent-green)',
                  display: 'grid',
                  placeItems: 'center',
                  fontFamily: 'var(--font-mono)',
                  fontWeight: 700,
                  fontSize: 22,
                  color: '#fff',
                  lineHeight: 1,
                  boxShadow: '0 0 24px rgba(112, 148, 36, 0.35)',
                  flexShrink: 0,
                }}
              >
                S
              </div>
              <div style={{ minWidth: 0 }}>
                <h2
                  id="about-title"
                  style={{
                    fontFamily: 'var(--font-sans)',
                    fontWeight: 700,
                    fontSize: 20,
                    letterSpacing: '-0.3px',
                    color: 'var(--accent-green)',
                    lineHeight: 1,
                    margin: 0,
                  }}
                >
                  StudioShell
                </h2>
                <div
                  style={{
                    fontFamily: 'var(--font-sans)',
                    fontSize: 11,
                    fontWeight: 500,
                    letterSpacing: '1.5px',
                    textTransform: 'uppercase',
                    color: 'var(--text-muted)',
                    marginTop: 8,
                    lineHeight: 1,
                  }}
                >
                  Acoustic Room Simulator
                </div>
              </div>
            </div>

            <p
              style={{
                margin: 0,
                fontSize: 13,
                lineHeight: 1.6,
                color: 'var(--text-primary)',
              }}
            >
              Design your studio in 3D, choose materials, place treatment, and
              see your RT60 in real time across six frequency bands. Real
              Sabine / Eyring math — no fake numbers, no signup, no telemetry.
            </p>

            {/* Credits strip */}
            <div
              style={{
                padding: '12px 14px',
                borderRadius: 'var(--radius-md)',
                background: 'var(--bg-subtle)',
                border: '1px solid var(--border-dim)',
                display: 'flex',
                flexDirection: 'column',
                gap: 'var(--space-sm)',
              }}
            >
              <CreditRow icon={<Icon icon={Code2} size={12} color="var(--text-hint)" />} label="Open source" value="MIT License" />
              <CreditRow icon={<Icon icon={User} size={12} color="var(--text-hint)" />} label="Made by" value="Fabio Torres" />
              <CreditRow icon={<Icon icon={BookOpen} size={12} color="var(--text-hint)" />} label="Physics" value="Sabine · Eyring · Millington" />
            </div>

            {/* Version strip */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 'var(--space-sm)' }}>
              <Stat label="Version" value="1.0.4" />
              <Stat label="Build" value="2026.04" />
              <Stat label="Engine" value="WebGL 2" />
            </div>

            {/* Actions */}
            <div style={{ display: 'flex', gap: 'var(--space-sm)', alignItems: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="secondary"
                size="sm"
                icon={<Icon icon={Coffee} size={14} />}
                onClick={() => window.open(KOFI_URL, '_blank', 'noopener')}
              >
                Donate <span style={{ opacity: 0.6, marginLeft: 2 }}>↗</span>
              </Button>
              <div style={{ flex: 1 }} />
              <Button variant="primary" size="sm" onClick={close}>
                Close
              </Button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/* ─── Credit row (icon · label · mono value) ───────────────────────── */

function CreditRow({
  icon,
  label,
  value,
}: {
  icon: ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '14px 1fr auto', gap: 10, alignItems: 'center' }}>
      {icon}
      <span
        style={{
          fontFamily: 'var(--font-sans)',
          fontSize: 10,
          fontWeight: 500,
          letterSpacing: '1.5px',
          textTransform: 'uppercase',
          color: 'var(--text-muted)',
          lineHeight: 1,
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontFamily: 'var(--font-mono)',
          fontSize: 11,
          color: 'var(--text-primary)',
          letterSpacing: 0,
          lineHeight: 1,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value}
      </span>
    </div>
  );
}

/* ─── Stat tile (Version/Build/Engine) ────────────────────────────── */

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div
      style={{
        background: 'var(--bg-panel-2)',
        border: '1px solid var(--border-mid)',
        borderRadius: 'var(--radius-md)',
        padding: '8px 10px',
      }}
    >
      <div
        style={{
          fontFamily: 'var(--font-sans)',
          fontSize: 10,
          fontWeight: 500,
          letterSpacing: '1.5px',
          textTransform: 'uppercase',
          color: 'var(--text-muted)',
          lineHeight: 1,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontFamily: 'var(--font-mono)',
          fontSize: 13,
          fontWeight: 500,
          color: 'var(--text-primary)',
          marginTop: 6,
          lineHeight: 1,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value}
      </div>
    </div>
  );
}
